import { Chip, Tooltip, Box, Typography, Stack } from '@mui/material';
import type { RaRecord } from 'react-admin';
import { InlinePair, fmtDateTimeShort } from '../../shared/listCells';
import { plNumber } from '../../shared/number';
import { calendarDate, calendarDatePassed, validityEnded } from '../../shared/dateTime';
import { useFeature } from '../featureFlags/FeatureGate';
import { driverShiftText } from '../userOffers/driverShift';
import { ABSENCE_CONSTRAINT, driverAvailableFromText } from '../userOffers/driverAvailableFrom';
import type { DriverShift } from '../userOffers/suggestionsApi';

export type LooseRecord = RaRecord & Record<string, unknown>;

const toText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/** Data bez godziny z kartoteki (północ UTC dnia) — dzień z zapisu, bez strefy. */
const formatDay = (v: unknown): string | null => {
  if (typeof v !== 'string' || v === '') return null;
  const text = calendarDate(v);
  return text === '—' ? null : text;
};

/** Nazwisko z imieniem i adresem służbowym pod spodem. */
export const PersonCell = ({ record }: { record: LooseRecord }) => (
  <Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {[record.surname, record.name].filter(Boolean).join(' ') || '—'}
    </Typography>
    {toText(record.email) && (
      <Typography variant="caption" color="text.secondary">
        {String(record.email)}
      </Typography>
    )}
  </Box>
);

/**
 * To samo co `PersonCell`, ale w jednej linii — na liście adres idzie obok
 * nazwiska (i do podpowiedzi), żeby wiersz nie miał dwóch pięter.
 */
export const PersonInlineCell = ({ record }: { record: LooseRecord }) => (
  <InlinePair
    primary={[record.surname, record.name].filter(Boolean).join(' ') || '—'}
    secondary={toText(record.email)}
    maxWidth={{ xs: 260, lg: 320, xl: 440 }}
  />
);

/**
 * Pojazd, w którym kierowca siedzi.
 *
 * Model dopuszcza więcej niż jeden, choć w danych klienta jest ich zawsze
 * zero albo jeden — nie spłaszczamy tego na siłę.
 */
export const Vehicle = ({ record }: { record: LooseRecord }) => {
  const cars = (record.cars as LooseRecord[] | undefined) ?? [];
  if (cars.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        bez pojazdu
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
      {cars.map((a) => (
        <Chip
          key={String(a.id)}
          size="small"
          variant="outlined"
          label={toText(a.registrationNumber) ?? `#${String(a.id)}`}
        />
      ))}
    </Stack>
  );
};

/** System pracy — wzorzec rotacji, np. „3 week / 1 week". */
export const WorkSystemCell = ({ record }: { record: LooseRecord }) => {
  const s = record.workingSystem as LooseRecord | undefined;
  if (!s) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return <span style={{ whiteSpace: 'nowrap' }}>{toText(s.name) ?? `#${String(s.id)}`}</span>;
};

/**
 * Termin zezwolenia na pracę.
 *
 * Zezwolenie dotyczy **wszystkich kierowców**, nie tylko obcokrajowców —
 * to zbiorcze uprawnienia (odpowiedzi Marathonu/Euvic 16.10.2026, pyt. 33c;
 * do 16.10 zakładaliśmy, że pole dotyczy tylko obcokrajowców, bo po terminie
 * byli też kierowcy z polskimi nazwiskami). Termin to data i obejmuje cały
 * swój dzień (pyt. 33f — potwierdzone).
 *
 * ⚠️ Pokazujemy FAKT („termin minął"), a nie wniosek („nie może pracować"):
 * podpowiedzi tego pola nie czytają (wykluczają tylko uprawnienia typu
 * Blokada z `/drivers/eligibilities/`), a o dopuszczeniu decyduje człowiek.
 */
export const WorkPermitCell = ({
  record,
  now = new Date(),
  inclusiveTerm,
}: {
  record: LooseRecord;
  now?: Date;
  /**
   * Termin włącznie — domyślnie z przełącznika `driverValidityInclusive`
   * (12.10.2026); jawna wartość tylko w testach i podglądach.
   */
  inclusiveTerm?: boolean;
}) => {
  const flag = useFeature('driverValidityInclusive');
  const inclusive = inclusiveTerm ?? flag;
  const raw = record.workPermissionUpTo;
  const formatted = formatDay(raw);
  if (!formatted) {
    return (
      <Typography variant="caption" color="text.secondary">
        brak daty
      </Typography>
    );
  }
  // Termin to data i obejmuje cały swój dzień w Polsce — jak uprawnienia
  // kierowcy i pojazdu w API (`validityEnded`, decyzja użytkownika 08.10.2026;
  // do 08.10 mijał z początkiem swojego dnia). Przełącznik wyłączony
  // (wersja „obecna”, 12.10.2026) — reguła sprzed 08.10, jak na produkcji.
  const expired = inclusive ? validityEnded(String(raw), now) : calendarDatePassed(String(raw), now);
  // Poprawa brzmienia 01.10.2026: pełne zdania, źródło to kartoteka TMS.
  return (
    <Tooltip
      title={
        expired
          ? 'Termin zezwolenia na pracę zapisany w kartotece TMS już minął. Zezwolenie dotyczy wszystkich kierowców — sprawdź w dziale kadr, czy jest nowe, i popraw termin w TMS.'
          : 'Termin z kartoteki TMS.'
      }
    >
      <Chip
        size="small"
        variant="outlined"
        color={expired ? 'warning' : 'default'}
        label={expired ? `termin minął ${formatted}` : formatted}
      />
    </Tooltip>
  );
};

/** „Dostępny od" z API — kształt z `user-offers/dostepnosc-kierowcy.ts`. */
export interface DriverAvailabilityUi {
  sources: string[];
  measuredAt: string | null;
  availableFrom: string;
  availableNow: boolean;
  constraint: string | null;
  description: string | null;
  remaining: {
    todayH: number | null;
    weekH: number | null;
    twoWeeksH: number | null;
    untilBreakH: number | null;
  };
  nextAbsence: { od: string; do: string; type: string | null } | null;
  zalozenia: string[];
  /** Bieżąca zmiana z osi czasu tachografu (06.10.2026); brak = same liczniki. */
  shift?: DriverShift | null;
}

export const availabilityOf = (record: LooseRecord): DriverAvailabilityUi | null =>
  record.availability && typeof record.availability === 'object'
    ? (record.availability as DriverAvailabilityUi)
    : null;

/**
 * „Dostępny od" — z limitów czasu jazdy (eco-truck) i nieobecności
 * z kartoteki TMS. Trzy stany i żaden nie udaje innego: dostępny teraz,
 * dostępny od chwili X z powodem, brak danych. **Brak danych NIE jest
 * „dostępny"** — bez świeżej telematyki i bez nieobecności nic nie wiemy.
 * Liczby z limitów jazdy to szacunek („ok.”), bo to założenia
 * ostrożnościowe (9 h / 11 h / 45 h bez ulg), nie odczyt z tachografu.
 * Nieobecność z kartoteki TMS to fakt — bez „ok.” i bez „szacunku”
 * (07.10.2026), tym samym tekstem co w rekomendacjach
 * (`driverAvailableFromText`): „nieobecny, dostępny od 13.10”.
 */
export const AvailableFromCell = ({ record }: { record: LooseRecord }) => {
  const d = availabilityOf(record);
  if (!d) {
    return (
      <Tooltip title="Nie ma aktualnego odczytu z telematyki ani nieobecności w kartotece TMS, więc nie da się tego oszacować.">
        <Typography variant="caption" color="text.secondary">
          brak danych
        </Typography>
      </Tooltip>
    );
  }
  // Dymek zdaniami (poprawa brzmienia 01.10.2026): kiedy odczyt i jakie
  // założenia — wcześniej łańcuch fragmentów rozdzielonych „ · ”.
  const details = [
    d.measuredAt ? `Odczyt z telematyki: ${fmtDateTimeShort(d.measuredAt)}.` : null,
    d.zalozenia.length > 0 ? `Założenia: ${d.zalozenia.join('; ')}.` : null,
  ]
    .filter((x): x is string => x !== null)
    .join(' ');
  // Druga linia z osi czasu tachografu (06.10.2026) — ta sama co przy
  // kandydacie w rekomendacjach; bez osi czasu nic nie dopisujemy.
  const shiftLine = d.shift ? (
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="driver-shift"
      sx={{ display: 'block' }}
    >
      {driverShiftText(d.shift)}
    </Typography>
  ) : null;
  if (d.availableNow) {
    return (
      <Box>
        <Tooltip title={details}>
          <Chip
            size="small"
            variant="outlined"
            color="success"
            label={
              d.remaining.todayH !== null
                ? `dostępny, dziś jeszcze ok. ${plNumber(d.remaining.todayH)} h jazdy`
                : 'dostępny (szacunek)'
            }
          />
        </Tooltip>
        {shiftLine}
      </Box>
    );
  }
  // Przy nieobecności dymek mówi tylko, skąd jest (kartoteka TMS, rodzaj
  // nieobecności) — założenia limitów jazdy dotyczą szacunku, nie faktu.
  const absent = d.constraint === ABSENCE_CONSTRAINT;
  return (
    <Box>
      <Tooltip
        title={[d.description ? `Powód: ${d.description}.` : null, absent ? null : details]
          .filter(Boolean)
          .join(' ')}
      >
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          label={driverAvailableFromText(d)}
        />
      </Tooltip>
      {shiftLine}
    </Box>
  );
};
